import * as THREE from 'three';
import React, { useMemo } from 'react';
import { correctAxisData, loadTexture } from '../../utils';
import * as constantsViewer from '../../constants';
import textureMdfNatural from 'assets/images/textures/mdfnatural2.jpg';
import textureMirror from 'assets/images/textures/mirror2.jpg';
import textureGlass from 'assets/images/textures/glass2.jpg';
import textureWhiteBeech from 'assets/images/textures/whitebeech_v.jpg';
import data from './data/intermediateShelf';
import {
  onPointerDown,
  onPointerUp,
  manageOnClickComponent,
} from '../../helpers';
import { useDispatch } from 'react-redux';

export function IntermediateShelf({
  intermediateShelf = null,
  cabinetId,
  shelfLevel,
  configuratorMode,
  yOffset = 0,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  shelfHeight = 370,
  unselected = false,
}) {
  return (
    <group>
      <IntermediateShelfMainPiece
        intermediateShelf={intermediateShelf.interShelf}
        cabinetId={cabinetId}
        shelfLevel={shelfLevel}
        configuratorMode={configuratorMode}
        interShelfLevel={intermediateShelf.position}
        yOffset={yOffset}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        shelfHeight={shelfHeight}
        unselected={unselected}
      />
      <IntermediateShelfSupport
        intermediateShelfSupport={intermediateShelf.getSupport('L')}
        supportDepth={intermediateShelf.getSupport('L').depth}
        yOffset={yOffset}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        shelfHeight={shelfHeight}
        unselected={unselected}
      />
      <IntermediateShelfSupport
        intermediateShelfSupport={intermediateShelf.getSupport('R')}
        supportDepth={intermediateShelf.getSupport('R').depth}
        yOffset={yOffset}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        shelfHeight={shelfHeight}
        unselected={unselected}
      />
    </group>
  );
}

function IntermediateShelfSupport({
  intermediateShelfSupport = null,
  supportDepth,
  yOffset = 0,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  shelfHeight = 370,
  unselected = false,
}) {
  let colorRef = intermediateShelfSupport.color.reference;
  let interShelfDepth = supportDepth;
  let interShelfHeight = intermediateShelfSupport.height;

  var modelCabinetDepth = 419;
  var modelCabinetWidth = 999;
  var cabinetDiffWith = cabinetWidth - modelCabinetWidth;

  var scaleX = cabinetWidth / modelCabinetWidth;
  var scaleZ = interShelfDepth / modelCabinetDepth;

  var scaleY = interShelfHeight / 185;
  scaleY = (185 * scaleY - 0) / 185;

  scaleX = (990 * scaleX - 36) / 964;
  scaleZ = (419 * scaleZ - 49) / 370;

  var interShelfDiffDepth = cabinetDepth - interShelfDepth;
  var interShelfDiffHeight = 185 - interShelfHeight;

  var posYDiff = shelfHeight / 2 - yOffset;
  posYDiff -= interShelfDiffHeight / 2;

  var xDiff = 0;
  switch (intermediateShelfSupport.position) {
    case 'L':
      xDiff -= cabinetDiffWith / 2;
      break;

    case 'R':
      xDiff += modelCabinetWidth + cabinetDiffWith / 2;
      xDiff -= 26;
      break;

    default:
      break;
  }

  /* 
  Remove this in meshPhongMaterial because onClick fired twice:
    side={THREE.DoubleSide} 
  */

  /*
  Other method to apply lookAt method on camera:
    const { camera } = useThree()
    camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
  */
  //const mesh = makePaneMesh();
  let paneIsTextured = false;

  if (
    intermediateShelfSupport &&
    (intermediateShelfSupport.material === 'I' ||
      intermediateShelfSupport.material === 'G' ||
      (intermediateShelfSupport.material === 'M' &&
        intermediateShelfSupport.color.reference === 'BRUT') ||
      (intermediateShelfSupport.material === 'M' &&
        intermediateShelfSupport.color.reference === 'H5474'))
  )
    paneIsTextured = true;
  return (
    <>
      <IntermediateShelfSupportTextured
        visible={paneIsTextured}
        intermediateShelfSupport={intermediateShelfSupport}
        scaleY={scaleY}
        scaleZ={scaleZ}
        posYDiff={posYDiff}
        posXDiff={xDiff}
        interShelfDiffDepth={interShelfDiffDepth}
        unselected={unselected}
      />
      <IntermediateShelfSupportUntextured
        visible={!paneIsTextured}
        intermediateShelfSupport={intermediateShelfSupport}
        scaleY={scaleY}
        scaleZ={scaleZ}
        posYDiff={posYDiff}
        posXDiff={xDiff}
        interShelfDiffDepth={interShelfDiffDepth}
        unselected={unselected}
      />
    </>
  );
}

function IntermediateShelfSupportUntextured({
  visible,
  intermediateShelfSupport = null,
  scaleY,
  scaleZ,
  posYDiff,
  posXDiff,
  interShelfDiffDepth,
  unselected = false,
}) {
  let color = 0xffffff;
  let specular = 0x919191;
  let shininess = 4.92458;
  let opacity = 1;
  if (intermediateShelfSupport) {
    color = intermediateShelfSupport.color.hexaNum;
    opacity = intermediateShelfSupport.color.opacity;
  }
  if (intermediateShelfSupport && intermediateShelfSupport.material === 'G') {
    color = constantsViewer.MATERIAL_GLASS_COLOR;
    specular = constantsViewer.MATERIAL_GLASS_SPECULAR;
    shininess = constantsViewer.MATERIAL_GLASS_SHININESS;
    opacity = constantsViewer.MATERIAL_GLASS_OPACITY;
  }
  return (
    <mesh
      visible={visible}
      renderOrder={
        intermediateShelfSupport && intermediateShelfSupport.material === 'G'
          ? constantsViewer.ZORDER_INTERSHELF_SUPPORT_GLASS
          : constantsViewer.ZORDER_INTERSHELF_SUPPORT
      }
      receiveShadow
      castShadow
      onUpdate={self =>
        updateMeshSupport(
          self,
          scaleY,
          scaleZ,
          posYDiff,
          interShelfDiffDepth,
          posXDiff,
        )
      }
    >
      {generateGeometrySupport()}
      <meshPhongMaterial
        attach="material"
        color={intermediateShelfSupport === null ? 0xffffff : color}
        specular={specular}
        shininess={shininess}
        opacity={
          unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : opacity
        }
        transparent
      />
    </mesh>
  );
}

function IntermediateShelfSupportTextured({
  visible,
  intermediateShelfSupport = null,
  scaleY,
  scaleZ,
  posYDiff,
  posXDiff,
  interShelfDiffDepth,
  unselected = false,
}) {
  const textureMdfNaturalLoaded = useMemo(
    () => loadTexture(textureMdfNatural),
    [textureMdfNatural],
  );

  const textureWhiteBeechLoaded = useMemo(
    () => loadTexture(textureWhiteBeech),
    [textureWhiteBeech],
  );

  const textureMirrorLoaded = useMemo(() => loadTexture(textureMirror), [
    textureMirror,
  ]);
  const textureGlassLoaded = useMemo(() => loadTexture(textureGlass), [
    textureGlass,
  ]);
  let texture = textureMdfNaturalLoaded;
  let color = intermediateShelfSupport.color.hexaNum;
  let specular = 0x919191;
  let shininess = 4.92458;

  let opacity = intermediateShelfSupport.color.opacity;

  if (intermediateShelfSupport.material === 'I') {
    texture = textureMirrorLoaded;
    color = constantsViewer.MATERIAL_MIRROR2_COLOR;
    specular = constantsViewer.MATERIAL_MIRROR2_SPECULAR;
    shininess = constantsViewer.MATERIAL_MIRROR2_SHININESS;
  } else if (
    intermediateShelfSupport.material === 'M' &&
    intermediateShelfSupport.color.reference === 'H5474'
  ) {
    texture = textureWhiteBeechLoaded;
  } else if (
    intermediateShelfSupport.material === 'M' &&
    intermediateShelfSupport.color.reference === 'BRUT'
  ) {
    texture = textureMdfNaturalLoaded;
    color = constantsViewer.MATERIAL_MDFNATURAL_COLOR;
  } else if (
    intermediateShelfSupport &&
    intermediateShelfSupport.material === 'G'
  ) {
    texture = textureGlassLoaded;
    color = constantsViewer.MATERIAL_GLASS_COLOR;
    specular = constantsViewer.MATERIAL_GLASS_SPECULAR;
    shininess = constantsViewer.MATERIAL_GLASS_SHININESS;
    opacity = constantsViewer.MATERIAL_GLASS_OPACITY;
  }
  return (
    <mesh
      visible={visible}
      renderOrder={
        intermediateShelfSupport && intermediateShelfSupport.material === 'G'
          ? constantsViewer.ZORDER_INTERSHELF_SUPPORT_GLASS
          : constantsViewer.ZORDER_INTERSHELF_SUPPORT_TEXTURED
      }
      receiveShadow
      castShadow
      onUpdate={self =>
        updateMeshSupport(
          self,
          scaleY,
          scaleZ,
          posYDiff,
          interShelfDiffDepth,
          posXDiff,
        )
      }
    >
      {generateGeometrySupport()}
      <meshPhongMaterial
        attach="material"
        color={color}
        specular={specular}
        shininess={shininess}
        depthWrite={false}
        opacity={
          unselected
            ? constantsViewer.OPACITY_UNSELECTED_SHELF
            : !intermediateShelfSupport
            ? 1
            : opacity
        }
        transparent
        map={texture}
        onUpdate={self => (self.needsUpdate = true)}
      />
    </mesh>
  );
}

function IntermediateShelfMainPiece({
  intermediateShelf = null,
  cabinetId,
  shelfLevel,
  configuratorMode,
  interShelfLevel,
  yOffset = 0,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  shelfHeight = 370,
  unselected = false,
}) {
  const dispatch = useDispatch();

  let colorRef = intermediateShelf.color.reference;
  let interShelfDepth = intermediateShelf.depth;

  var modelCabinetDepth = 419;
  var modelCabinetWidth = 999;
  var scaleX = cabinetWidth / modelCabinetWidth;
  var scaleZ = interShelfDepth / modelCabinetDepth;

  scaleX = (990 * scaleX - 27) / 972;
  scaleZ = (419 * scaleZ - 36) / 380;

  var interShelfDiffDepth = cabinetDepth - interShelfDepth;
  var posYDiff = shelfHeight / 2 - yOffset;
  /* 
  Remove this in meshPhongMaterial because onClick fired twice:
    side={THREE.DoubleSide} 
  */

  /*
  Other method to apply lookAt method on camera:
    const { camera } = useThree()
    camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
  */
  //const mesh = makePaneMesh();
  let paneIsTextured = false;

  if (
    intermediateShelf &&
    (intermediateShelf.material === 'I' ||
      intermediateShelf.material === 'G' ||
      (intermediateShelf.material === 'M' &&
        intermediateShelf.color.reference === 'BRUT'))
  )
    paneIsTextured = true;
  return (
    <group
      onPointerDown={e => onPointerDown(e)}
      onPointerUp={e =>
        onPointerUp(e, e => {
          manageOnClickComponent(
            e,
            unselected,
            intermediateShelf,
            cabinetId,
            dispatch,
            configuratorMode,
            shelfLevel,
            undefined,
            interShelfLevel,
          );
        })
      }
    >
      <IntermediateShelfTextured
        visible={paneIsTextured}
        intermediateShelf={intermediateShelf}
        scaleX={scaleX}
        scaleZ={scaleZ}
        posYDiff={posYDiff}
        interShelfDiffDepth={interShelfDiffDepth}
        unselected={unselected}
      />
      <IntermediateShelfUntextured
        visible={!paneIsTextured}
        intermediateShelf={intermediateShelf}
        scaleX={scaleX}
        scaleZ={scaleZ}
        posYDiff={posYDiff}
        interShelfDiffDepth={interShelfDiffDepth}
        unselected={unselected}
      />
    </group>
  );
}

function IntermediateShelfUntextured({
  visible,
  intermediateShelf = null,
  scaleX,
  scaleZ,
  posYDiff,
  interShelfDiffDepth,
  unselected = false,
}) {
  let color = 0xffffff;
  let specular = 0x919191;
  let shininess = 4.92458;
  let opacity = 1;
  if (intermediateShelf) {
    color = intermediateShelf.color.hexaNum;
    opacity = intermediateShelf.color.opacity;
  }
  if (intermediateShelf && intermediateShelf.material === 'G') {
    color = constantsViewer.MATERIAL_GLASS_COLOR;
    specular = constantsViewer.MATERIAL_GLASS_SPECULAR;
    shininess = constantsViewer.MATERIAL_GLASS_SHININESS;
    opacity = constantsViewer.MATERIAL_GLASS_OPACITY;
  }
  return (
    <mesh
      visible={visible}
      renderOrder={constantsViewer.ZORDER_INTERSHELF}
      receiveShadow
      castShadow
      onUpdate={self =>
        updateMeshMainPiece(self, scaleX, scaleZ, posYDiff, interShelfDiffDepth)
      }
    >
      {generateGeometryMainPiece()}
      <meshPhongMaterial
        attach="material"
        color={intermediateShelf === null ? 0xffffff : color}
        specular={specular}
        shininess={shininess}
        opacity={
          unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : opacity
        }
        transparent
      />
    </mesh>
  );
}

function IntermediateShelfTextured({
  visible,
  intermediateShelf = null,
  scaleX,
  scaleZ,
  posYDiff,
  interShelfDiffDepth,
  unselected = false,
}) {
  const textureMdfNaturalLoaded = useMemo(
    () => loadTexture(textureMdfNatural),
    [textureMdfNatural],
  );

  const textureMirrorLoaded = useMemo(() => loadTexture(textureMirror), [
    textureMirror,
  ]);
  const textureGlassLoaded = useMemo(() => loadTexture(textureGlass), [
    textureGlass,
  ]);
  let texture = textureMdfNaturalLoaded;
  let color = intermediateShelf.color.hexaNum;
  let specular = 0x919191;
  let shininess = 4.92458;

  let opacity = intermediateShelf.color.opacity;
  let isLowHeight = false;

  if (intermediateShelf.material === 'I') {
    texture = textureMirrorLoaded;
    color = constantsViewer.MATERIAL_MIRROR2_COLOR;
    specular = constantsViewer.MATERIAL_MIRROR2_SPECULAR;
    shininess = constantsViewer.MATERIAL_MIRROR2_SHININESS;
    isLowHeight = true;
  } else if (
    intermediateShelf.material === 'M' &&
    intermediateShelf.color.reference === 'BRUT'
  ) {
    texture = textureMdfNaturalLoaded;
    color = constantsViewer.MATERIAL_MDFNATURAL_COLOR;
  } else if (intermediateShelf && intermediateShelf.material === 'G') {
    texture = textureGlassLoaded;
    color = constantsViewer.MATERIAL_GLASS_COLOR;
    specular = constantsViewer.MATERIAL_GLASS_SPECULAR;
    shininess = constantsViewer.MATERIAL_GLASS_SHININESS;
    opacity = constantsViewer.MATERIAL_GLASS_OPACITY;
    isLowHeight = true;
  }
  return (
    <mesh
      visible={visible}
      renderOrder={constantsViewer.ZORDER_INTERSHELF}
      receiveShadow
      castShadow
      onUpdate={self =>
        updateMeshMainPiece(
          self,
          scaleX,
          scaleZ,
          posYDiff,
          interShelfDiffDepth,
          isLowHeight,
        )
      }
    >
      {generateGeometryMainPiece()}
      <meshPhongMaterial
        attach="material"
        color={color}
        specular={specular}
        shininess={shininess}
        opacity={
          unselected
            ? constantsViewer.OPACITY_UNSELECTED_SHELF
            : !intermediateShelf
            ? 1
            : opacity
        }
        transparent
        map={texture}
        onUpdate={self => (self.needsUpdate = true)}
      />
    </mesh>
  );
}

function updateMeshMainPiece(
  self,
  scaleX,
  scaleZ,
  posYDiff,
  interShelfDiffDepth,
  isLowHeight = false,
) {
  //  self = self.children[0];
  var center = new THREE.Vector3();
  self.geometry.computeBoundingBox();
  self.geometry.boundingBox.getCenter(center);
  self.geometry.center();
  self.position.copy(center);
  let size = new THREE.Vector3();
  self.geometry.boundingBox.getSize(size);

  let scaleY = 1;
  if (isLowHeight) scaleY = 0.5;

  self.geometry.scale(scaleX, scaleY, scaleZ);
  self.position.z -= interShelfDiffDepth / 2;
  self.position.y -= posYDiff;
  if (isLowHeight) self.position.y -= 2;
  //self.position.z += paneSideDepth / 2 - cabinetDiffDepth / 2 + 10;
}

function updateMeshSupport(
  self,
  scaleY,
  scaleZ,
  posYDiff,
  interShelfDiffDepth,
  posXDiff,
) {
  //  self = self.children[0];
  var center = new THREE.Vector3();
  self.geometry.computeBoundingBox();
  self.geometry.boundingBox.getCenter(center);
  self.geometry.center();
  self.position.copy(center);
  let size = new THREE.Vector3();
  self.geometry.boundingBox.getSize(size);

  self.geometry.scale(1, scaleY, scaleZ);
  self.position.z -= interShelfDiffDepth / 2;

  self.position.y -= posYDiff;
  self.position.x += posXDiff;
}

function generateGeometryMainPiece() {
  var vertices = new Float32Array(data.intermediateShelf.vertices);
  var normals = new Float32Array(data.intermediateShelf.normals);
  var uv_coord = new Float32Array(data.intermediateShelf.uv_coord);
  return (
    <bufferGeometry attach="geometry">
      <bufferAttribute
        attachObject={['attributes', 'position']}
        count={vertices.length / 3}
        array={correctAxisData(vertices)}
        itemSize={3}
      />
      <bufferAttribute
        attachObject={['attributes', 'normal']}
        count={normals.length / 3}
        array={correctAxisData(normals)}
        itemSize={3}
      />
      <bufferAttribute
        attachObject={['attributes', 'uv']}
        count={uv_coord.length / 2}
        array={uv_coord}
        itemSize={2}
      />
    </bufferGeometry>
  );
}

function generateGeometrySupport() {
  var vertices = new Float32Array(data.intermediateShelfSupport.vertices);
  var normals = new Float32Array(data.intermediateShelfSupport.normals);
  var uv_coord = new Float32Array(data.intermediateShelfSupport.uv_coord);
  return (
    <bufferGeometry attach="geometry">
      <bufferAttribute
        attachObject={['attributes', 'position']}
        count={vertices.length / 3}
        array={correctAxisData(vertices)}
        itemSize={3}
      />
      <bufferAttribute
        attachObject={['attributes', 'normal']}
        count={normals.length / 3}
        array={correctAxisData(normals)}
        itemSize={3}
      />
      <bufferAttribute
        attachObject={['attributes', 'uv']}
        count={uv_coord.length / 2}
        array={uv_coord}
        itemSize={2}
      />
    </bufferGeometry>
  );
}
